/**
 * Affiliate attribution (client side). When a visitor lands on any page with a
 * `?ref=CODE` query param we stash the code in localStorage with an expiry, then attach
 * it to the eventual /auth/register call. First-touch wins (we don't overwrite an
 * existing un-expired code) and it's cleared once a signup consumes it.
 */
const KEY = 'sg_ref';
const TTL_DAYS = 60; // mirrors the default affiliateCookieDays; server is the source of truth.

interface StoredRef {
  code: string;
  exp: number;
}

/** Read `?ref=` from the current URL and persist it (first-touch, with expiry). */
export function captureAffiliateRef(): void {
  try {
    const code = new URLSearchParams(window.location.search).get('ref')?.trim();
    if (!code) return;
    if (getAffiliateRef()) return; // first touch wins — don't overwrite a live code
    const ref: StoredRef = { code: code.slice(0, 32), exp: Date.now() + TTL_DAYS * 86_400_000 };
    localStorage.setItem(KEY, JSON.stringify(ref));
  } catch {
    /* private mode / storage disabled — attribution is best-effort */
  }
}

/** The stored referral code, or undefined if absent/expired. */
export function getAffiliateRef(): string | undefined {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return undefined;
    const ref = JSON.parse(raw) as StoredRef;
    if (!ref.code || Date.now() > ref.exp) {
      localStorage.removeItem(KEY);
      return undefined;
    }
    return ref.code;
  } catch {
    return undefined;
  }
}

/** Clear the stored code (call after a signup consumes it). */
export function clearAffiliateRef(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
